import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../hooks/useAuth';

const GitHubTokenSection: React.FC = () => {
  const { user, githubConnected, setGitHubToken, clearToken, loading } = useAuth();
  const [tokenInput, setTokenInput] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const handleSave = async () => {
    if (!tokenInput.trim()) {
      setLocalError('Please enter a GitHub token');
      return;
    }
    setLocalError(null);
    try {
      await setGitHubToken(tokenInput.trim());
      setTokenInput('');
    } catch (err: any) {
      setLocalError(err.message || 'Failed to save GitHub token');
    }
  };

  return (
    <>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          GitHub Access
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connect a GitHub personal access token when you need repository and translation operations.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <Link
            href="https://github.com/settings/tokens/new?scopes=repo,read:user&description=TranslatorX%20App"
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
          >
            Create GitHub token (repo + read:user)
          </Link>
        </Typography>

        {githubConnected ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Connected as <strong>{user?.login}</strong>
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No GitHub token connected. Repository-backed features will be disabled.
          </Alert>
        )}

        {localError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {localError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
          <TextField
            fullWidth
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
            label="GitHub Personal Access Token"
            disabled={loading}
          />
          <Button variant="contained" onClick={handleSave} disabled={loading || !tokenInput.trim()}>
            Save
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setConfirmClearOpen(true)}
            disabled={loading || !githubConnected}
          >
            Clear
          </Button>
        </Box>
      </Paper>

      <Dialog open={confirmClearOpen} onClose={() => setConfirmClearOpen(false)}>
        <DialogTitle>Clear GitHub Token</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Clear the saved GitHub token? Repository access will be disconnected until you add it again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClearOpen(false)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              clearToken();
              setConfirmClearOpen(false);
            }}
          >
            Clear Token
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GitHubTokenSection;
